import React, { useMemo } from 'react';
import { 
  GitBranch, 
  GitCommit, 
  ArrowRight,
  CheckCircle,
  Circle,
  AlertCircle,
  Clock
} from 'lucide-react';
import { Task } from '../../lib/db';
import TaskPriorityBadge from './TaskPriorityBadge';

interface TaskDependencyGraphProps {
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
}

interface TaskNode {
  task: Task;
  level: number;
  dependencies: string[];
  dependents: string[];
}

function TaskDependencyGraph({ tasks, onTaskClick }: TaskDependencyGraphProps) {
  // Build dependency graph
  const taskGraph = useMemo(() => {
    const nodes: Map<string, TaskNode> = new Map();
    
    // Initialize nodes
    tasks.forEach(task => {
      nodes.set(task.id, {
        task,
        level: 0,
        dependencies: task.depends_on || [],
        dependents: []
      });
    });
    
    // Build dependents list
    tasks.forEach(task => {
      if (task.depends_on) {
        task.depends_on.forEach(depId => {
          const depNode = nodes.get(depId);
          if (depNode) {
            depNode.dependents.push(task.id);
          }
        });
      }
    });
    
    // Calculate levels (topological sort)
    const calculateLevel = (nodeId: string, visited: Set<string> = new Set()): number => {
      if (visited.has(nodeId)) return 0;
      visited.add(nodeId);
      
      const node = nodes.get(nodeId);
      if (!node) return 0;
      
      if (node.dependencies.length === 0) {
        node.level = 0;
        return 0;
      }
      
      const maxDepLevel = Math.max(
        ...node.dependencies.map(depId => calculateLevel(depId, visited))
      );
      
      node.level = maxDepLevel + 1;
      return node.level;
    };
    
    tasks.forEach(task => calculateLevel(task.id));
    
    return nodes;
  }, [tasks]);

  // Group tasks by level
  const tasksByLevel = useMemo(() => {
    const levels: Map<number, TaskNode[]> = new Map();
    
    taskGraph.forEach(node => {
      if (!levels.has(node.level)) {
        levels.set(node.level, []);
      }
      levels.get(node.level)!.push(node);
    });
    
    return Array.from(levels.entries())
      .sort(([a], [b]) => a - b)
      .map(([level, nodes]) => ({
        level,
        nodes: nodes.sort((a, b) => {
          // Sort by status (incomplete first), then priority
          if (a.task.status === 'Complete' && b.task.status !== 'Complete') return 1;
          if (a.task.status !== 'Complete' && b.task.status === 'Complete') return -1;
          
          const priorityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
          return (priorityOrder[a.task.priority || 'Low'] || 3) - 
                 (priorityOrder[b.task.priority || 'Low'] || 3);
        })
      }));
  }, [taskGraph]);

  const getStatusIcon = (task: Task) => {
    if (task.status === 'Complete') return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (!task.is_live) return <AlertCircle className="w-5 h-5 text-amber-500" />;
    if (task.status === 'In Progress') return <Clock className="w-5 h-5 text-blue-500" />;
    return <Circle className="w-5 h-5 text-gray-400" />;
  };

  const getDependencyTypeIcon = (task: Task) => {
    if (task.dependency_type === 'sequential') 
      return <GitBranch className="w-4 h-4 text-purple-500" />;
    if (task.dependency_type === 'parallel') 
      return <GitCommit className="w-4 h-4 text-blue-500" />;
    return null;
  };

  if (tasks.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No tasks to display in dependency graph
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-full p-4">
        {tasksByLevel.map(({ level, nodes }) => (
          <div key={level} className="mb-8">
            {/* Level header */}
            <div className="flex items-center mb-4">
              <div className="text-sm font-medium text-gray-500 mr-4">
                Level {level}
              </div>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Tasks at this level */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {nodes.map(({ task }) => {
                const node = taskGraph.get(task.id)!;
                
                return (
                  <div
                    key={task.id}
                    onClick={() => onTaskClick?.(task)}
                    className={`relative bg-white border rounded-lg p-4 transition-all cursor-pointer
                      ${task.is_live ? 'border-gray-200 hover:border-blue-400 hover:shadow-md' : 
                        'border-amber-200 bg-amber-50 opacity-75'}`}
                  >
                    {/* Task content */}
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        {getStatusIcon(task)}
                        {getDependencyTypeIcon(task) && (
                          <div className="absolute -bottom-1 -right-1">
                            {getDependencyTypeIcon(task)}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <h4 className={`font-medium text-sm ${
                          task.status === 'Complete' ? 'text-gray-500 line-through' : 'text-gray-900'
                        }`}>
                          {task.title}
                        </h4>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <TaskPriorityBadge priority={task.priority} />
                          {task.deadline && (
                            <span className="text-xs text-gray-500">
                              Due {new Date(task.deadline).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Dependencies */}
                    {node.dependencies.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="text-xs text-gray-500 mb-1">Depends on:</div>
                        <div className="flex flex-wrap gap-1">
                          {node.dependencies.map(depId => {
                            const depTask = tasks.find(t => t.id === depId);
                            if (!depTask) return null;
                            
                            return (
                              <span
                                key={depId}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                                  depTask.status === 'Complete' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {depTask.title}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Flow indicator for sequential dependencies */}
                    {task.dependency_type === 'sequential' && node.dependents.length > 0 && (
                      <div className="absolute -right-8 top-1/2 transform -translate-y-1/2">
                        <ArrowRight className="w-6 h-6 text-purple-400" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Legend */}
        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-gray-600">Complete</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">To Do</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <span className="text-gray-600">Blocked</span>
            </div>
            <div className="flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-purple-500" />
              <span className="text-gray-600">Sequential</span>
            </div>
            <div className="flex items-center gap-2">
              <GitCommit className="w-4 h-4 text-blue-500" />
              <span className="text-gray-600">Parallel</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskDependencyGraph;